import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ZoomInIcon } from 'lucide-react';
import { useBackNavigation } from '../hooks/useBackNavigation';
export function AleksandroNevsky() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const goBack = useBackNavigation('/architecture');
  return <div className="w-full min-h-screen bg-stone-50" data-id="element-68">
      {/* Header */}
      <header className="py-8 px-6 bg-white border-b border-stone-200 sticky top-0 z-50" data-id="element-69">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-70">
          <button onClick={goBack} className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-id="element-71">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-72" />
            <span className="font-light" data-id="element-73">Back</span>
          </button>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-74">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-emerald-50/30 to-stone-50" data-id="element-75">
        <div className="max-w-5xl mx-auto" data-id="element-76">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-77">
            <p className="text-emerald-700 font-light tracking-wide uppercase mb-4" data-id="element-78">
              Architecture
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-79">
              Aleksandro-Nevsky Manufacture
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-80">
              Class-A business center project development within a business
              complex redevelopment programme
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-81">
              <p className="text-lg" data-id="element-82">
                <span className="font-normal text-stone-900" data-id="element-83">Role:</span>{' '}
                Marketing / User Experience / Interior Planning → Product Manager 
              </p>
              <p className="text-lg" data-id="element-84">
                <span className="font-normal text-stone-900" data-id="element-85">Phase:</span>{' '}
                Conception → Specification (Current)
              </p>
              <p className="text-lg" data-id="element-86">
                <span className="font-normal text-stone-900" data-id="element-87">Timeline:</span>{' '}
                2024 - Present (~1 year conception, ongoing specification)
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Overview */}
      <section className="py-20 px-6 bg-white" data-id="element-88">
        <div className="max-w-5xl mx-auto" data-id="element-89">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-90">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-91">
              Project Overview
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-92">
              <p data-id="element-93">
                Aleksandro-Nevsky Manufacture (ANM), a business center
                management company, is developing a new Class-A office building
                to expand their portfolio. I joined the project at the
                Conception stage as a marketing, user experience, and interior
                planning consultant, later transitioning to Project Coordinator
                and Administrator during the Specification and Development
                stages.
              </p>
              <p data-id="element-94">
                My responsibilities extended beyond administration to include
                analyzing and correcting propositions and development plans,
                liaising between stakeholders to find optimal solutions that
                balanced the budget, investor requests, future user needs, city
                regulations, modern trends, and contractor ambitions.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Vertical Composition - Current Development */}
      <section className="py-12 px-6 bg-stone-50" data-id="element-95">
        <div className="max-w-7xl mx-auto" data-id="element-96">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-97">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-98">
              Vertical Composition
              <span className="ml-3 text-sm text-emerald-700 font-normal uppercase tracking-wide" data-id="element-99">
                Under Development
              </span>
            </h2>
            {/* Aerial Render */}
            <div className="mb-8" data-id="element-100">
              <div className="relative overflow-hidden rounded-2xl shadow-xl cursor-pointer group" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/ready_anm/2.jpg')} data-id="element-101">
                <img src="https://storage.yandexcloud.net/maria-images/images/ready_anm/2.jpg" alt="Aerial view showing the proposed vertical tower integration" className="w-full h-auto" data-id="element-102" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-103">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-104" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-105">
                Aerial view showing the proposed vertical tower integration
              </p>
            </div>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-106">
              The project is currently at the Specification stage, awaiting
              approval from official bodies. Following the initial application
              for approval by the City Head of Architecture, we reimagined the
              building to address feedback regarding facades and cityscape
              integration. This led to the development of a vertical composition
              that resolves conflicts between parking space and greenery
              requirements in city regulations, while creating a stronger
              architectural presence within the complex.
            </p>
            {/* Development Work */}
            <div data-id="element-107">
              <h3 className="text-xl font-light text-stone-900 mb-6" data-id="element-108">
                Development Studies
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-109">
                {[{
                src: 'https://storage.yandexcloud.net/maria-images/images/ready_anm/%D0%92%D0%B5%D1%80%D1%82%D0%B8%D0%BA%D0%B0%D0%BB%D1%8C%D0%BD%D0%B0%D1%8F%20%D0%BA%D0%BE%D0%BC%D0%BF%D0%BE%D0%B7%D0%B8%D1%86%D0%B8%D1%8F-06.png',
                title: 'Tower Elevations',
                description: 'Multiple facade studies exploring material and compositional variations'
              }, {
                src: 'https://storage.yandexcloud.net/maria-images/images/ready_anm/%D0%B2%D0%B5%D1%80%D1%82%20%D0%BA%D0%BE%D0%BC%D0%BF%201%20%D1%8D%D1%82%D0%B0%D0%B6.png',
                title: 'Initial Floor Plan Outline',
                description: 'Preliminary spatial organization for the vertical option'
              }].map((item, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: index * 0.1
              }} className="group cursor-pointer" onClick={() => setSelectedImage(item.src)} data-id="element-110">
                    <div className="relative overflow-hidden rounded-xl shadow-lg bg-white" data-id="element-111">
                      <img src={item.src} alt={item.title} className="w-full h-auto" data-id="element-112" />
                      <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-113">
                        <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-114" />
                      </div>
                    </div>
                    <div className="mt-4" data-id="element-115">
                      <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-116">
                        {item.title}
                      </h4>
                      <p className="text-stone-600 font-light text-sm" data-id="element-117">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Horizontal Composition - Original Development */}
      <section className="py-12 px-6 bg-white" data-id="element-118">
        <div className="max-w-7xl mx-auto" data-id="element-119">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-120">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-121">
              Horizontal Composition
              <span className="ml-3 text-sm text-stone-700 font-normal uppercase tracking-wide" data-id="element-122">
                Original Development
              </span>
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-123">
              Initial design featuring a horizontal building form with
              comprehensive technical documentation. The design prioritized user
              experience through detailed spatial planning before exploring
              formal and material approaches.
            </p>
            {/* Elevations and Sections */}
            <div className="mb-12" data-id="element-124">
              <h3 className="text-2xl font-light text-stone-900 mb-6" data-id="element-125">
                Elevations & Sections
              </h3>
              <div className="grid grid-cols-1 gap-8" data-id="element-126">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/ready_anm/12.%D0%A0%D0%90%D0%97%D0%A0%D0%95%D0%97%D0%AB%20(1)-1.png')} data-id="element-127">
                  <div className="relative overflow-hidden rounded-xl shadow-lg bg-white p-4" data-id="element-128">
                    <img src="https://storage.yandexcloud.net/maria-images/images/ready_anm/12.%D0%A0%D0%90%D0%97%D0%A0%D0%95%D0%97%D0%AB%20(1)-1.png" alt="Building Sections" className="w-full h-auto" data-id="element-129" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-130">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-131" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-132">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-133">
                      Building Sections
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-134">
                      Detailed cross-sections showing vertical organization and
                      spatial relationships
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
            {/* Technical Planning - User First */}
            <div className="mb-16" data-id="element-135">
              <h3 className="text-2xl font-light text-stone-900 mb-3" data-id="element-136">
                Technical Planning
                <span className="ml-3 text-sm text-stone-600 font-normal" data-id="element-137">
                  User-First Approach
                </span>
              </h3>
              <p className="text-stone-600 font-light mb-4 max-w-3xl" data-id="element-138">
                Comprehensive technical drawings developed to understand spatial
                organization, circulation patterns, and functional requirements
                before formal design exploration.
              </p>
              <p className="text-stone-600 font-light mb-8 max-w-3xl" data-id="element-139">
                During this phase, we identified the opportunity to implement
                automated parking systems, saving valuable space while offering
                a unique value proposition and satisfying city regulations. We
                secured contractors capable of executing this complex solution.
              </p>
              {/* Floor Plans */}
              <div data-id="element-140">
                <h4 className="text-lg font-light text-stone-900 mb-6" data-id="element-141">
                  Floor Plans
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-142">
                  {[{
                  src: 'https://storage.yandexcloud.net/maria-images/images/ready_anm/04.%D0%9F%D0%9B-2%20(1)-1.png',
                  title: 'First Floor Plan',
                  description: 'Ground level layout with main entrance and circulation'
                }, {
                  src: 'https://storage.yandexcloud.net/maria-images/images/ready_anm/03.%D0%9F%D0%9B-1%20(1)-1.png',
                  title: 'Basement Floor Plan',
                  description: 'Parking and service areas'
                }, {
                  src: 'https://storage.yandexcloud.net/maria-images/images/ready_anm/07.%D0%9F%D0%9B-5-1.png',
                  title: 'Fourth Floor Plan',
                  description: 'Office spaces and amenities'
                }, {
                  src: 'https://storage.yandexcloud.net/maria-images/images/ready_anm/11.%D0%9F%D0%9B-9%20(1)-1.png',
                  title: 'Eighth Floor Plan',
                  description: 'Upper level office and garden configuration'
                }].map((plan, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6,
                  delay: index * 0.1
                }} className="group cursor-pointer" onClick={() => setSelectedImage(plan.src)} data-id="element-143">
                      <div className="relative overflow-hidden rounded-xl shadow-lg bg-white p-4" data-id="element-144">
                        <img src={plan.src} alt={plan.title} className="w-full h-auto" data-id="element-145" />
                        <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-146">
                          <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-147" />
                        </div>
                      </div>
                      <div className="mt-4" data-id="element-148">
                        <h5 className="text-lg font-light text-stone-900 mb-2" data-id="element-149">
                          {plan.title}
                        </h5>
                        <p className="text-stone-600 font-light text-sm" data-id="element-150">
                          {plan.description}
                        </p>
                      </div>
                    </motion.div>)}
                </div>
              </div>
            </div>
            {/* Design Options & Development */}
            <div className="mb-16" data-id="element-151">
              <h3 className="text-2xl font-light text-stone-900 mb-4" data-id="element-152">
                Formal Design Exploration
              </h3>
              <p className="text-stone-600 font-light mb-8 max-w-3xl" data-id="element-153">
                Following the technical planning phase, we evaluated four
                propositions from local architecture firms, selecting the
                winning design based on relevance, visual appeal, and budget
                optimization. We collaborated with the selected architect on
                interior planning to ensure the building could be sold or leased
                in both vertical and horizontal blocks.
              </p>
              {/* Initial Options */}
              <div className="mb-12" data-id="element-154">
                <h4 className="text-lg font-light text-stone-900 mb-6" data-id="element-155">
                  Initial Design Options
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-156">
                  {[{
                  src: 'https://storage.yandexcloud.net/maria-images/images/ready_anm/image%20(25).png',
                  title: 'Option A - Curved Form',
                  description: 'Initial exploration featuring a curved facade responding to site geometry'
                }, {
                  src: 'https://storage.yandexcloud.net/maria-images/images/ready_anm/13.%D0%A4%D0%90%D0%A1%D0%90%D0%94%D0%AB%20(1)%20(1)-1.png',
                  title: 'Option B - Linear Form',
                  description: 'Alternative approach with extended horizontal massing and rhythmic facade'
                }].map((option, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6,
                  delay: index * 0.1
                }} className="group cursor-pointer" onClick={() => setSelectedImage(option.src)} data-id="element-157">
                      <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-158">
                        <img src={option.src} alt={option.title} className="w-full h-auto" data-id="element-159" />
                        <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-160">
                          <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-161" />
                        </div>
                      </div>
                      <div className="mt-4" data-id="element-162">
                        <h5 className="text-lg font-light text-stone-900 mb-2" data-id="element-163">
                          {option.title}
                        </h5>
                        <p className="text-stone-600 font-light text-sm" data-id="element-164">
                          {option.description}
                        </p>
                      </div>
                    </motion.div>)}
                </div>
              </div>
              {/* Option B Development */}
              <div className="mb-12" data-id="element-165">
                <h4 className="text-lg font-light text-stone-900 mb-6" data-id="element-166">
                  Option B - Further Development
                </h4>
                <p className="text-stone-600 font-light mb-6 max-w-3xl" data-id="element-167">
                  Following initial review, Option B was selected for further
                  development. The design was refined to enhance the facade
                  rhythm and material detailing while maintaining the horizontal
                  composition.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-168">
                  {[{
                  src: 'https://storage.yandexcloud.net/maria-images/images/ready_anm/image%20(27).png',
                  title: 'River-Facing Render',
                  description: 'Refined facade design with contemporary glass and metal detailing'
                }, {
                  src: 'https://storage.yandexcloud.net/maria-images/images/ready_anm/image%20(26).png',
                  title: 'Back Side Render',
                  description: 'Integration strategy with existing structures and urban context'
                }].map((render, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6,
                  delay: index * 0.1
                }} className="group cursor-pointer" onClick={() => setSelectedImage(render.src)} data-id="element-169">
                      <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-170">
                        <img src={render.src} alt={render.title} className="w-full h-auto bg-gradient-to-br from-stone-100 to-stone-200" data-id="element-171" />
                        <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-172">
                          <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-173" />
                        </div>
                      </div>
                      <div className="mt-4" data-id="element-174">
                        <h5 className="text-lg font-light text-stone-900 mb-2" data-id="element-175">
                          {render.title}
                        </h5>
                        <p className="text-stone-600 font-light text-sm" data-id="element-176">
                          {render.description}
                        </p>
                      </div>
                    </motion.div>)}
                </div>
              </div>
              {/* Final Horizontal Render */}
              <div data-id="element-177">
                <h4 className="text-lg font-light text-stone-900 mb-6" data-id="element-178">
                  Final Horizontal Composition
                </h4>
                <p className="text-stone-600 font-light mb-6 max-w-3xl" data-id="element-179">
                  This photorealistic render represents the culmination of the
                  horizontal design development, presented to the city
                  architectural committee and client before the decision to
                  explore the vertical composition approach.
                </p>
                <div className="relative overflow-hidden rounded-2xl shadow-xl cursor-pointer group" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/ready_anm/facade%20anm%20render-1.png')} data-id="element-180">
                  <img src="https://storage.yandexcloud.net/maria-images/images/ready_anm/facade%20anm%20render-1.png" alt="Final horizontal composition" className="w-full h-auto" data-id="element-181" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-182">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-183" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-184">
                  Final horizontal composition presented before vertical
                  re-direction
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Research & Development */}
      <section className="py-12 px-6 bg-stone-50" data-id="element-185">
        <div className="max-w-7xl mx-auto" data-id="element-186">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-187">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-188">
              Research & Development
            </h2>
            <p className="text-lg text-stone-600 font-light mb-8 max-w-3xl" data-id="element-189">
              Comprehensive research phase including market analysis, site
              studies, and aesthetic exploration that informed the design
              direction and programmatic decisions.
            </p>
            {/* Market Positioning & Development Proposition */}
            <div className="mb-12" data-id="element-190">
              <h3 className="text-xl font-light text-stone-900 mb-6" data-id="element-191">
                Market Positioning & Strategy
              </h3>
              <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed mb-8" data-id="element-192">
                <p data-id="element-193">
                  We explored various solutions for portfolio expansion,
                  including art/science clusters and leasing accommodations.
                  This research revealed a local market gap in Class-A office
                  spaces, informing the strategic direction of the development.
                </p>
                <p data-id="element-194">
                  Based on these findings, we formed the preliminary branding
                  and identity, delivering research results and territorial
                  renewal concepts to the contracted architect for developing
                  the new Class-A building design.
                </p>
              </div>
            </div>
            {/* Aesthetic Research & Market Analysis */}
            <div className="mb-12" data-id="element-195">
              <h3 className="text-xl font-light text-stone-900 mb-6" data-id="element-196">
                Design Precedents & Market Analysis
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-197">
                {/* Left Column - Massing */}
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/ready_anm/concepts.png')} data-id="element-198">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-199">
                    <img src="https://storage.yandexcloud.net/maria-images/images/ready_anm/concepts.png" alt="Architectural References - Massing" className="w-full h-auto" data-id="element-200" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-201">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-202" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-203">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-204">
                      Architectural References - Massing
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-205">
                      AI-generated office building renders exploring form,
                      scale, and urban integration
                    </p>
                  </div>
                </motion.div>
                {/* Right Column - Detail & Market */}
                <div className="space-y-8" data-id="element-206">
                  <motion.div initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6,
                  delay: 0.1
                }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/ready_anm/image_2025-11-10_20-44-52.png')} data-id="element-207">
                    <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-208">
                      <img src="https://storage.yandexcloud.net/maria-images/images/ready_anm/image_2025-11-10_20-44-52.png" alt="Architectural References - Detail" className="w-full h-auto" data-id="element-209" />
                      <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-210">
                        <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-211" />
                      </div>
                    </div>
                    <div className="mt-4" data-id="element-212">
                      <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-213">
                        Architectural References - Detail
                      </h4>
                      <p className="text-stone-600 font-light text-sm" data-id="element-214">
                        Material studies and entrance design explorations for
                        Class-A office environments
                      </p>
                    </div>
                  </motion.div>
                  <motion.div initial={{
                  opacity: 0,
                  y: 30
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.6,
                  delay: 0.2
                }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/ready_anm/1.%20%D0%9F%D1%80%D0%B5%D0%B4%D0%BF%D1%80%D0%BE%D0%B5%D0%BA%D1%82%D0%BD%D0%BE%D0%B5%20%D0%B8%D1%81%D1%81%D0%BB%D0%B5%D0%B4%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5_15.01.2024%20(2)-10.png')} data-id="element-215">
                    <div className="relative overflow-hidden rounded-xl shadow-lg bg-white p-4" data-id="element-216">
                      <img src="https://storage.yandexcloud.net/maria-images/images/ready_anm/1.%20%D0%9F%D1%80%D0%B5%D0%B4%D0%BF%D1%80%D0%BE%D0%B5%D0%BA%D1%82%D0%BD%D0%BE%D0%B5%20%D0%B8%D1%81%D1%81%D0%BB%D0%B5%D0%B4%D0%BE%D0%B2%D0%B0%D0%BD%D0%B8%D0%B5_15.01.2024%20(2)-10.png" alt="Market Research" className="w-full h-auto" data-id="element-217" />
                      <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-218">
                        <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-219" />
                      </div>
                    </div>
                    <div className="mt-4" data-id="element-220">
                      <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-221">
                        Market Research
                      </h4>
                      <p className="text-stone-600 font-light text-sm" data-id="element-222">
                        Office real estate market analysis by district - Q3 2023
                        data informing programmatic decisions
                      </p>
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
            {/* Project Context */}
            <div data-id="element-223">
              <h3 className="text-xl font-light text-stone-900 mb-6" data-id="element-224">
                Project Context & Development Rationale
              </h3>
              <div className="grid grid-cols-1 gap-8" data-id="element-225">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/ready_anm/image_2025-11-07_12-42-30.png')} data-id="element-226">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-227">
                    <img src="https://storage.yandexcloud.net/maria-images/images/ready_anm/image_2025-11-07_12-42-30.png" alt="Bridge Development Context" className="w-full h-auto" data-id="element-228" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-229">
                      <ZoomInIcon className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-230" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-231">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-232">
                      Infrastructure Development - New Bridge Construction
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-233">
                      Major bridge construction project connecting the
                      Aleksandro-Nevsky complex to expanded urban development,
                      driving the need for increased commercial space and
                      portfolio expansion
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Image Modal */}
      {selectedImage && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-stone-900/95 z-50 flex items-center justify-center p-6" onClick={() => setSelectedImage(null)} data-id="element-234">
          <motion.img initial={{
        scale: 0.9
      }} animate={{
        scale: 1
      }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain rounded-lg" data-id="element-235" />
          <button className="absolute top-6 right-6 text-white text-4xl font-light hover:text-emerald-400 transition-colors" onClick={() => setSelectedImage(null)} data-id="element-236">
            ×
          </button>
        </motion.div>}
    </div>;
}